import {
  AloftPriceJSON,
  ListingType,
  OrderFeature,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyData,
  PropertyDataWithOptionalReadonly,
  SourceType,
  UnitRateModels,
  defaultUnitRateModelFeatureKeys,
  expected,
  getReadonlySourceDescription,
  unitRateModelFeatureKeyToSubjectKey,
} from '@aloft/toolkit-shared';
import { Block, Flex, Grid, Icon, theme } from '@aloft/toolkit-sui';

import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useParams } from 'react-router';

import { useOrchestrateAutofillPropertyAPI } from '../../hooks/api/toolkitComponents.ts';
import { logger } from '../../logging/logging.ts';
import {
  selectHasArvOption,
  selectIncludeCostApproach,
  selectOrderFeatures,
  selectSalesComparisonApproach,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAivArvActiveTab,
  selectPropertyGridExpandedPropertyIds,
} from '../../redux/features/selectors/uiSelectors.ts';
import { selectUnitRates } from '../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../redux/types.ts';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary.tsx';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen.tsx';
import { ColumnErrorBoundaryFallback } from './ColumnErrorBoundaryFallback.tsx';
import { HeaderPropertyGridSection } from './columnSections/HeaderPropertyGridSection.tsx';
import { PropertyGridColumnSubHeader } from './columnSections/PropertyGridColumnSubHeader.tsx';
import { GRID_BORDER, LABEL_COLUMN_WIDTH } from './consts.ts';
import {
  PropertyGridColumnDefinition,
  PropertyGridColumnSectionComponent,
  PropertyGridRowFieldType,
  PropertyGridSectionProps,
  PropertyGridType,
} from './types.ts';

const log = logger();

type PropertyGridColumnProps = Pick<PropertyGridColumnDefinition, 'propertyId' | 'type'> & {
  columnSections: PropertyGridColumnSectionComponent[];
  fieldTypes: PropertyGridRowFieldType[];
  gridType: PropertyGridType;
  isDataSourceDrawerOpen: boolean;
  columnIndex: number;
  dataSourceDrawerEnabled: boolean;
  listingType?: ListingType;
} & Pick<PropertyGridSectionProps, 'control' | 'getValues' | 'setValue'>;

export const PropertyGridColumn = ({
  columnSections,
  fieldTypes,
  gridType,
  isDataSourceDrawerOpen,
  propertyId,
  type,
  columnIndex,
  dataSourceDrawerEnabled,
  listingType,
  control,
  getValues,
  setValue,
}: PropertyGridColumnProps) => {
  const orderId = expected(useParams().orderId);
  const isRentalComparables = listingType === 'rentalListing';

  const orderFeatures = useAppSelector(selectOrderFeatures, shallowEqual);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const propertyGridExpandedPropertyIds = useAppSelector(selectPropertyGridExpandedPropertyIds);
  const propertyIsExpanded = propertyGridExpandedPropertyIds[gridType] === propertyId;

  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const isPropertyColumn = type === 'property';
  // Only the subject's data-source columns in AdjustmentsGrid use unit rate models directly.
  // Comp data-source columns continue to use autofill orchestration.
  const isAdjustmentsGridSourceColumn =
    !isPropertyColumn && gridType === 'AdjustmentsGrid' && isSubject;

  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, propertyId));
  const propertyData = isSubject
    ? (propertyDataState as SubjectPropertyDataState).subjectData
    : (propertyDataState as CompPropertyDataState).compData;

  const hasUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';

  // For FeatureGrid source columns and rental subject columns, use autofill orchestration.
  const { data: autofillOrchestrationData, isLoading: autofillOrchestrationIsLoading } =
    useOrchestrateAutofillPropertyAPI<PropertyData>(
      {
        pathParams: { orderID: orderId, propertyId },
        // @ts-expect-error - Type mismatch because ToolkitContext['fetcherOptions'] is of type Record<string, string>
        queryParams: {
          sources: [type],
          includeBackupListingData: true,
          prefillWithDefaultValues: false,
          ...(!isPropertyColumn && { sources: [type] }),
          ...(listingType && { listingType }),
        },
      },
      {
        enabled: (!isPropertyColumn || isRentalComparables) && !isAdjustmentsGridSourceColumn,
      },
    );

  // For all source columns, read pre-fetched data from readonlyData.sources in Redux (populated at comp/subject creation time).
  const readonlySourcesState = useMemo(() => {
    if (isPropertyColumn || autofillOrchestrationIsLoading) return 'pending';
    if (!autofillOrchestrationData) return 'unavailable';

    const sources = autofillOrchestrationData.readonlyData?.sources;

    if (!sources) return 'pending';
    if (!sources[type as keyof typeof sources]) return 'unavailable';
    return 'ready';
  }, [isPropertyColumn, autofillOrchestrationData, autofillOrchestrationIsLoading, type]);

  const sourceDescription =
    readonlySourcesState === 'ready' && autofillOrchestrationData
      ? getReadonlySourceDescription(autofillOrchestrationData.readonlyData, type as SourceType)
      : undefined;

  const unitRateModels = useAppSelector(selectUnitRates);
  const parsedUnitRateModels: UnitRateModels | null = useMemo(
    () => (isAdjustmentsGridSourceColumn ? unitRateModels : null),
    [isAdjustmentsGridSourceColumn, unitRateModels],
  );

  // Build a PropertyData-compatible object from unit rate models for AdjustmentsGrid source columns.
  const unitRateModelPropertyData = useMemo((): PropertyDataWithOptionalReadonly | undefined => {
    if (!isAdjustmentsGridSourceColumn || !parsedUnitRateModels) return undefined;
    const models = parsedUnitRateModels;

    const unitRates = defaultUnitRateModelFeatureKeys.reduce<Record<string, AloftPriceJSON>>(
      (acc, featureKey) => {
        const subjectKey = unitRateModelFeatureKeyToSubjectKey[featureKey];
        let rate: AloftPriceJSON | null | undefined;
        if (type === 'regression') {
          if (models.regression?.status === 'success') {
            rate = models.regression.output.unitRates[featureKey].rate;
          }
        } else if (type === 'sensitivity') {
          rate = models.sensitivityAnalysis?.[featureKey]?.selectedRate.rate;
        }
        // Skip null/undefined — a null regression rate means the feature was dropped from the model.
        if (rate != null) {
          return { ...acc, [subjectKey]: rate };
        }
        return acc;
      },
      {},
    );

    return {
      $type: 'PropertyData',
      version: 1,
      editableData: {
        salesComparison: { unitRates },
      },
    } as unknown as PropertyDataWithOptionalReadonly;
  }, [isAdjustmentsGridSourceColumn, parsedUnitRateModels, type]);

  const isFeatureGrid = gridType === 'FeatureGrid';

  const autofillData = isAdjustmentsGridSourceColumn
    ? unitRateModelPropertyData
    : autofillOrchestrationData;
  const autofillIsLoading = isAdjustmentsGridSourceColumn
    ? false
    : readonlySourcesState === 'pending';
  const isLoading = isPropertyColumn ? !propertyData : autofillIsLoading;
  const sourceUnavailable =
    !isLoading && isFeatureGrid && !isPropertyColumn && readonlySourcesState === 'unavailable';

  const formFieldName = `columns.${columnIndex}` as const;

  useEffect(() => {
    if (autofillData && (!isPropertyColumn || isRentalComparables)) {
      setValue(formFieldName, { propertyData: autofillData, propertyId, type });
    }
  }, [
    autofillData,
    formFieldName,
    isPropertyColumn,
    isRentalComparables,
    listingType,
    propertyId,
    setValue,
    type,
  ]);

  useEffect(() => {
    if (propertyData && isPropertyColumn && !(isRentalComparables && autofillData)) {
      setValue(formFieldName, {
        propertyData,
        propertyId,
        type,
      });
    }
  }, [autofillData, propertyData, formFieldName, isPropertyColumn, isRentalComparables, setValue]);

  const columnIsVisible = isPropertyColumn || propertyIsExpanded;

  const columnIsSticky =
    isPropertyColumn &&
    (isDataSourceDrawerOpen ? propertyIsExpanded : propertyId === PROPERTY_GRID_SUBJECT_PROPERTY);

  // Memoize fieldTypes to ensure referential stability for children (PropertyGridRow),
  // as PropertyGrid creates a new array on every render.
  const stableFieldTypes = useMemo(() => fieldTypes, [fieldTypes.join(',')]);

  const gridSectionProps: PropertyGridSectionProps = {
    fieldTypes: stableFieldTypes,
    gridType,
    columnIndex,
    control,
    getValues,
    setValue,
    hasUnitRate,
  };

  const sortedOrderFeatures: OrderFeature[] = orderFeatures
    .filter((feature) => feature !== 'manualCost')
    .sort((a, b) => a.localeCompare(b));
  if (includeCostApproach && orderFeatures.includes('manualCost')) {
    sortedOrderFeatures.push('manualCost');
  }

  let nbOfFieldColumns = !isPropertyColumn ? 1 : fieldTypes.length;
  if (isFeatureGrid && hasArvOption && (!isSubject || activeTab !== 'all')) nbOfFieldColumns -= 1;
  if (gridType === 'SensitivityGrid') nbOfFieldColumns = 1;

  const hasPaddingBetweenColumns = gridType !== 'SensitivityGrid';

  return (
    <ErrorBoundary
      Fallback={ColumnErrorBoundaryFallback}
      onError={() => log.fatal(`Failed to display Property Grid Column ${propertyId} - ${type}`)}
    >
      <Flex
        zIndex={columnIsSticky ? theme.zIndices.sticky : theme.zIndices.base}
        marginLeft={isPropertyColumn && hasPaddingBetweenColumns ? '12px' : '0'}
        backgroundColor="surface1"
        flexDirection="column"
        height="100%"
        __dangerDoNotUseSx={{
          opacity: isDataSourceDrawerOpen && !propertyIsExpanded ? 0.5 : 1,
          display: columnIsVisible ? 'flex' : 'none',
          borderRight: columnIsVisible ? GRID_BORDER : 'none',
          borderBottom: columnIsVisible ? GRID_BORDER : 'none',
          borderLeft: columnIsVisible ? GRID_BORDER : 'none',
          borderTop: 'none',
        }}
        {...(columnIsSticky
          ? {
              position: 'sticky',
              left: hasPaddingBetweenColumns
                ? `calc(${LABEL_COLUMN_WIDTH} + 12px)`
                : LABEL_COLUMN_WIDTH,
            }
          : {})}
      >
        <HeaderPropertyGridSection
          columnIndex={columnIndex}
          gridType={gridType}
          control={control}
          dataSourceDrawerEnabled={dataSourceDrawerEnabled}
          listingType={listingType}
        />

        {isLoading && (
          <Block height="100%" width="100%" position="relative">
            <LoadingScreen />
          </Block>
        )}

        {!isLoading && sourceUnavailable && (
          <Flex
            height="100%"
            width="100%"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            textAlign="center"
            padding="16px"
          >
            <Icon icon="error-warning" color="alertErrorLight" size="xxl" />
            <Block __dangerDoNotUseSx={{ fontSize: '12px', color: theme.colors.textSecondary }}>
              Data could not be fetched for this source
            </Block>
          </Flex>
        )}

        {!isLoading && !sourceUnavailable && (
          <Grid
            backgroundColor="strokeDefault"
            gap="1px"
            gridTemplateColumns={`repeat(${nbOfFieldColumns}, 1fr)`}
          >
            <PropertyGridColumnSubHeader
              gridType={gridType}
              columnIndex={columnIndex}
              control={control}
              getValues={getValues}
              setValue={setValue}
              unitRateModels={parsedUnitRateModels}
              sourceDescription={sourceDescription}
            />
            {columnSections.map((Section) => (
              <Section
                key={`${propertyId}-${type}-${Section.name}-section`}
                {...gridSectionProps}
              />
            ))}
          </Grid>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
